import { ColumnDef, Row } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowUpDown, Clock, Repeat } from 'lucide-react';
import { InvoiceStatus } from '@/components/table/columns/invoice/invoice-status';
import { InvoiceType } from '@/components/table/columns/invoice/invoice-type';
import { getDateFormat } from '@/lib/date-formatter';
import { getCurrencyFormatter } from '@/lib/currency-formatter';
import { TInvoiceData } from '@/types/invoice';
import { InvoiceActions } from './invoice-actions';
import { ExtendedUser } from '@/types/next-auth';
import { TCurrency } from '@/types';
import { Checkbox } from '@/components/ui/checkbox';

export const defineInvoiceColumns = (
  t: (key: string) => string,
  user: ExtendedUser
): ColumnDef<TInvoiceData>[] => {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'client',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="px-0 min-w-28 justify-start"
          aria-label="Client name"
        >
          {t('tableHeaders.client')}
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }: { row: Row<TInvoiceData> }) => {
        const name: string = row.getValue('client');
        const isRecurring = row.original.isRecurring;

        return (
          <div className="flex items-center gap-2 min-w-0 max-w-[180px] md:max-w-40">
            <span className="truncate">
              {name.length > 20 ? `${name.substring(0, 20)}...` : name}
            </span>
            {isRecurring && (
              <Badge
                variant="secondary"
                className="shrink-0 gap-1 px-1.5 py-0 text-xs"
                title={t('recurring')}
              >
                <Repeat className="h-3 w-3" />
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'invoiceNumber',
      header: ({ column }) => (
        <Button
          aria-label="Invoice number"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="px-0 min-w-24 justify-start"
        >
          {t('tableHeaders.invoiceNumber')}
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }: { row: Row<TInvoiceData> }) => {
        return (
          <div className="capitalize min-w-28">
            {row.getValue('invoiceNumber')}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <Button
          aria-label="Invoice status"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="px-0 min-w-24 justify-start"
        >
          {t('tableHeaders.status')}

          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }: { row: Row<TInvoiceData> }) => {
        const status = row.getValue('status') as TInvoiceData['status'];
        const claimAt = row.original.clientPaidClaimAt;
        return (
          <div className="flex items-center gap-2">
            <InvoiceStatus status={status} />
            {claimAt && status !== 'PAID' && (
              <span
                className="relative inline-flex h-5 w-5 shrink-0"
                title={t('clientClaimedPaidTooltip')}
                aria-label={t('clientClaimedPaid')}
              >
                <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full border border-amber-400 bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
                  <Clock className="h-3 w-3" />
                </span>
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'invoiceType',
      header: ({ column }) => (
        <Button
          aria-label="Invoice type"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="px-0 min-w-24 justify-start"
        >
          {t('tableHeaders.invoiceType')}
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }: { row: Row<TInvoiceData> }) => (
        <InvoiceType type={row.getValue('invoiceType')} />
      ),
    },
    {
      accessorKey: 'serviceFrom',
      header: ({}) => (
        <div className="min-w-56">{t('tableHeaders.serviceFrom')}</div>
      ),
      cell: ({ row }: { row: Row<TInvoiceData> }) => (
        <div className="capitalize">
          {getDateFormat(row.getValue('serviceFrom'))}
          {row.getValue('serviceTo')
            ? ` - ${getDateFormat(row.getValue('serviceTo'))}`
            : ''}
        </div>
      ),
    },
    {
      accessorKey: 'serviceTo',
      header: ({}) => <div className="hidden"></div>,
      cell: ({ row }: { row: Row<TInvoiceData> }) => (
        <div className="hidden">{getDateFormat(row.getValue('serviceTo'))}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'issuedDate',
      header: ({ column }) => (
        <Button
          aria-label="Issued date"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="px-0 min-w-28 justify-start"
        >
          {t('tableHeaders.issuedDate')}
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }: { row: Row<TInvoiceData> }) => (
        <div className="capitalize">
          {getDateFormat(row.getValue('issuedDate'))}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'dueDate',
      header: ({}) => (
        <div className="min-w-28">{t('tableHeaders.dueDate')}</div>
      ),
      cell: ({ row }: { row: Row<TInvoiceData> }) => (
        <div className="capitalize ">
          {getDateFormat(row.getValue('dueDate'))}
        </div>
      ),
    },
    {
      accessorKey: 'amount',
      header: ({ column }) => (
        <Button
          aria-label="Amount"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="px-0 min-w-28 justify-start"
        >
          {t('tableHeaders.amount')}
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }: { row: Row<TInvoiceData> }) => {
        const currency: TCurrency = user.currency ?? 'EUR';
        const amount = parseFloat(row.getValue('amount'));

        const formatted = getCurrencyFormatter(amount, currency);

        return <div className="capitalize min-w-28">{formatted}</div>;
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }: { row: Row<TInvoiceData> }) => (
        <div className="flex justify-end">
          <InvoiceActions invoice={row.original} t={t} />
        </div>
      ),
    },
  ];
};
